/**
 * authorizationService.js
 *
 * Tracks Medicaid/payer service authorizations per individual.
 * Stores in Firestore `authorizations` collection.
 *
 * Each authorization document:
 *   id, individualId, organization,
 *   payerId, payerName,
 *   serviceType, billingCode,
 *   unitsApproved, unitsUsed, unitType ("15-min" | "encounter" | "day"),
 *   startDate, endDate, authNumber,
 *   status ("active" | "pending" | "expired" | "exhausted" | "denied"),
 *   notes, createdBy, createdAt, updatedAt
 */

import {
  collection, doc, setDoc, updateDoc, deleteDoc,
  query, where, orderBy, onSnapshot, getDocs,
  serverTimestamp, Timestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import { orgCollection, orgDoc } from "./orgPaths";
import { logAuditEvent } from "./auditService";

const COLL = "authorizations";

// ─── Helpers ─────────────────────────────────────────────────────

function getOrg() {
  try {
    const raw = localStorage.getItem("fieldnotes_user_profile");
    if (raw) return JSON.parse(raw).organization || "default-org";
  } catch (e) {}
  return localStorage.getItem("fieldnotes_user_org") || "default-org";
}

function genId() {
  return `auth_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
}

function toDateStr(d) {
  if (!d) return null;
  if (d.toDate) return d.toDate().toISOString().split("T")[0];
  return new Date(d).toISOString().split("T")[0];
}

// ─── CRUD ────────────────────────────────────────────────────────

/**
 * Create a new authorization.
 */
export async function createAuthorization(data) {
  const id = genId();
  const org = getOrg();
  const record = {
    id,
    individualId: data.individualId,
    organization: org,
    payerId: data.payerId || "",
    payerName: data.payerName || "Georgia Medicaid",
    serviceType: data.serviceType || "",
    billingCode: data.billingCode || "",
    unitsApproved: parseInt(data.unitsApproved) || 0,
    unitsUsed: 0,
    unitType: data.unitType || "15-min", // "15-min", "encounter", "day"
    startDate: data.startDate || new Date().toISOString().split("T")[0],
    endDate: data.endDate || "",
    authNumber: data.authNumber || "",
    status: data.status || "active",
    notes: data.notes || "",
    createdBy: data.createdBy || "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(orgDoc("authorizations", id), record);

  try {
    await logAuditEvent("AUTH_CREATED", {
      individualId: data.individualId,
      authId: id,
      serviceType: data.serviceType,
      unitsApproved: record.unitsApproved,
    });
  } catch (e) {}

  return record;
}

/**
 * Update an authorization.
 */
export async function updateAuthorization(authId, updates) {
  const ref = orgDoc("authorizations", authId);
  await updateDoc(ref, {
    ...updates,
    updatedAt: serverTimestamp(),
  });

  try {
    await logAuditEvent("AUTH_UPDATED", { authId, changes: Object.keys(updates) });
  } catch (e) {}
}

/**
 * Delete an authorization.
 */
export async function deleteAuthorization(authId) {
  await deleteDoc(orgDoc("authorizations", authId));
}


// ─── Queries ─────────────────────────────────────────────────────

/**
 * Real-time listener for an individual's authorizations.
 */
export function onIndividualAuthorizations(individualId, callback) {
  const q = query(
    orgCollection("authorizations"),
    where("individualId", "==", individualId),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, snap => {
    const auths = snap.docs.map(d => ({ ...d.data(), id: d.id }));
    // Auto-update expired status
    const today = new Date().toISOString().split("T")[0];
    const processed = auths.map(a => {
      if (a.status === "active" && a.endDate && a.endDate < today) {
        return { ...a, status: "expired" };
      }
      if (a.status === "active" && a.unitsUsed >= a.unitsApproved && a.unitsApproved > 0) {
        return { ...a, status: "exhausted" };
      }
      return a;
    });
    callback(processed);
  }, err => {
    console.error("Auth listener error:", err);
    callback([]);
  });
}

/**
 * Get all active authorizations for an individual + service type.
 * Used when submitting a note to check authorization.
 */
export async function getActiveAuthorization(individualId, billingCode) {
  const org = getOrg();
  const today = new Date().toISOString().split("T")[0];

  const q = query(
    orgCollection("authorizations"),
    where("individualId", "==", individualId),
    where("status", "==", "active")
  );

  const snap = await getDocs(q);
  const auths = snap.docs.map(d => d.data());

  // Find matching auth: same billing code, not expired, has remaining units
  return auths.find(a =>
    a.billingCode === billingCode &&
    (!a.endDate || a.endDate >= today) &&
    (!a.startDate || a.startDate <= today) &&
    (a.unitsApproved === 0 || a.unitsUsed < a.unitsApproved)
  ) || null;
}

/**
 * Get all org-wide authorizations (for admin dashboard).
 */
export function onOrgAuthorizations(callback) {
  const q = query(
    orgCollection("authorizations"),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ ...d.data(), id: d.id })));
  }, err => {
    console.error("Org auth listener error:", err);
    callback([]);
  });
}


// ─── Unit Tracking ───────────────────────────────────────────────

/**
 * Consume units from an authorization when a note is submitted.
 * Returns { success, warning?, auth } or { success: false, error }.
 */
export async function consumeUnits(authId, unitsToConsume) {
  const ref = orgDoc("authorizations", authId);

  // Read current to check
  const snap = await getDocs(query(orgCollection("authorizations"), where("id", "==", authId)));
  if (snap.empty) return { success: false, error: "Authorization not found" };

  const auth = snap.docs[0].data();
  const newUsed = (auth.unitsUsed || 0) + unitsToConsume;
  const remaining = auth.unitsApproved - newUsed;

  const updates = {
    unitsUsed: newUsed,
    updatedAt: serverTimestamp(),
  };

  // Auto-exhaust
  if (remaining <= 0 && auth.unitsApproved > 0) {
    updates.status = "exhausted";
  }

  await updateDoc(ref, updates);

  try {
    await logAuditEvent("AUTH_UNITS_CONSUMED", {
      authId,
      unitsConsumed: unitsToConsume,
      totalUsed: newUsed,
      remaining: Math.max(0, remaining),
    });
  } catch (e) {}

  const result = { success: true, auth: { ...auth, ...updates, unitsUsed: newUsed } };

  // Warnings
  if (auth.unitsApproved > 0) {
    const pctUsed = (newUsed / auth.unitsApproved) * 100;
    if (remaining <= 0) {
      result.warning = "Authorization exhausted — no units remaining";
    } else if (pctUsed >= 90) {
      result.warning = `Authorization 90%+ used — ${remaining} units remaining`;
    } else if (pctUsed >= 80) {
      result.warning = `Authorization 80%+ used — ${remaining} units remaining`;
    }
  }

  return result;
}


// ─── Check Authorization on Note Submit ──────────────────────────

/**
 * Pre-submit authorization check. Called from SubmittedPage.
 * Returns { authorized, auth?, warning?, error? }
 */
export async function checkAuthorization(individualId, billingCode, unitsNeeded) {
  const auth = await getActiveAuthorization(individualId, billingCode);

  if (!auth) {
    return {
      authorized: false,
      error: `No active authorization found for ${billingCode}. Note can still be submitted but may not be billable.`,
    };
  }

  const remaining = auth.unitsApproved - auth.unitsUsed;
  const today = new Date().toISOString().split("T")[0];

  // Check expiration
  if (auth.endDate && auth.endDate < today) {
    return {
      authorized: false,
      auth,
      error: `Authorization expired on ${auth.endDate}. Reauthorization needed.`,
    };
  }

  // Check units
  if (auth.unitsApproved > 0 && unitsNeeded > remaining) {
    return {
      authorized: false,
      auth,
      warning: `Only ${remaining} units remaining on auth (need ${unitsNeeded}). Note can still be submitted.`,
    };
  }

  // Check if nearing exhaustion
  const pctAfter = auth.unitsApproved > 0
    ? ((auth.unitsUsed + unitsNeeded) / auth.unitsApproved) * 100
    : 0;

  const result = { authorized: true, auth };

  if (pctAfter >= 80) {
    result.warning = `Authorization at ${Math.round(pctAfter)}% after this session — ${remaining - unitsNeeded} units will remain`;
  }

  return result;
}


// ─── Expiration Alerts ───────────────────────────────────────────

/**
 * Get authorizations expiring within N days (for compliance dashboard).
 */
export async function getExpiringAuthorizations(daysOut = 30) {
  const today = new Date();
  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + daysOut);
  const futureDateStr = futureDate.toISOString().split("T")[0];
  const todayStr = today.toISOString().split("T")[0];

  const q = query(
    orgCollection("authorizations"),
    where("status", "==", "active")
  );

  const snap = await getDocs(q);
  return snap.docs
    .map(d => d.data())
    .filter(a => a.endDate && a.endDate >= todayStr && a.endDate <= futureDateStr)
    .sort((a, b) => a.endDate.localeCompare(b.endDate));
}

/**
 * Get authorizations nearing exhaustion (80%+ used).
 */
export async function getNearExhaustedAuthorizations() {
  const q = query(
    orgCollection("authorizations"),
    where("status", "==", "active")
  );

  const snap = await getDocs(q);
  return snap.docs
    .map(d => d.data())
    .filter(a => a.unitsApproved > 0 && (a.unitsUsed / a.unitsApproved) >= 0.8)
    .sort((a, b) => (b.unitsUsed / b.unitsApproved) - (a.unitsUsed / a.unitsApproved));
}


// ─── Common Payers for Georgia ───────────────────────────────────

export const GEORGIA_PAYERS = [
  { id: "ga-medicaid", name: "Georgia Medicaid (DCH)" },
  { id: "amerigroup", name: "Amerigroup (GA Families)" },
  { id: "caresource", name: "CareSource Georgia" },
  { id: "peachstate", name: "Peach State Health Plan" },
  { id: "wellcare", name: "WellCare of Georgia" },
  { id: "unitedhc", name: "UnitedHealthcare Community Plan" },
  { id: "anthem", name: "Anthem Blue Cross GA" },
  { id: "aetna", name: "Aetna Better Health of GA" },
  { id: "self-pay", name: "Self-Pay" },
  { id: "other", name: "Other" },
];

// ─── Service types that typically require authorization ──────────

export const AUTH_REQUIRED_SERVICES = [
  { code: "H0039", label: "ICM / ACT", unitType: "15-min", typicalUnits: 480 },
  { code: "H2017", label: "PSR", unitType: "15-min", typicalUnits: 960 },
  { code: "H2015", label: "CST", unitType: "15-min", typicalUnits: 480 },
  { code: "H0038", label: "Peer Support", unitType: "15-min", typicalUnits: 240 },
  { code: "H2011", label: "Crisis Intervention", unitType: "15-min", typicalUnits: 128 },
  { code: "90837", label: "Therapy (53+ min)", unitType: "encounter", typicalUnits: 52 },
  { code: "90834", label: "Therapy (38-52 min)", unitType: "encounter", typicalUnits: 52 },
  { code: "90832", label: "Therapy (16-37 min)", unitType: "encounter", typicalUnits: 52 },
  { code: "99213", label: "Med Management", unitType: "encounter", typicalUnits: 12 },
];